import { graphql } from '~publish/gql'
import { useLazyQuery, type ApolloError } from '@apollo/client'
import type {
  CommentData,
  CommentThreadQueryVariables,
} from '~publish/gql/graphql'

const CommentThreadQueryGQL = graphql(/* GraphQL */ `
  query CommentThread($input: CommentThreadInput!) {
    commentThread(input: $input) {
      thread {
        ... on BaseComment {
          __typename
          id
          text
          publishedAt
          status
          parentId
          serviceType
          isOwnReply
          assets {
            id
            mimeType
            source
            thumbnail
            type
          }
          author {
            username
            name
            avatar
          }
          post {
            id
            text
            dueAt
            channelService
            assets {
              ...PostMediaAsset_Asset
            }
            tags {
              name
              color
            }
            metadata {
              ... on CommonPostMetadata {
                type
              }
            }
          }
        }
        ... on CommentFromThirdParty {
          labels
          suggestions {
            text
            source
          }
          sentimentAnalysis {
            type
          }
        }
        ... on CommentFromBuffer {
          publishingStatus {
            status
          }
        }
      }
    }
  }
`)

type CommentThreadQueryResult = {
  commentThread: {
    thread: CommentData[]
  }
}

type UseCommentThreadResult = {
  getCommentThread: (commentId: string) => Promise<{
    originalComment: CommentData | null
    context: CommentData[]
    reply: CommentData | null
  }>
  loading: boolean
  error: ApolloError | null
}

const isCommentData = (comment: unknown): comment is CommentData => {
  return (
    comment !== null && typeof comment === 'object' && '__typename' in comment
  )
}

export const useCommentThread = (): UseCommentThreadResult => {
  const [getThread, { loading, error }] = useLazyQuery<
    CommentThreadQueryResult,
    CommentThreadQueryVariables
  >(CommentThreadQueryGQL)

  const getCommentThread = async (
    commentId: string,
  ): Promise<{
    originalComment: CommentData | null
    context: CommentData[]
    reply: CommentData | null
  }> => {
    const { data } = await getThread({
      variables: {
        input: {
          commentId,
        },
      },
    })

    const thread = data?.commentThread?.thread ?? []
    const originalCommentIndex = thread.findIndex(
      (comment) => isCommentData(comment) && comment.id === commentId,
    )
    const originalComment =
      originalCommentIndex !== -1 ? thread[originalCommentIndex] : null
    const context = thread.slice(0, originalCommentIndex).filter(isCommentData)
    const nextComment =
      originalCommentIndex !== -1 && originalCommentIndex < thread.length - 1
        ? thread[originalCommentIndex + 1]
        : null

    return {
      originalComment: isCommentData(originalComment) ? originalComment : null,
      context,
      reply: isCommentData(nextComment) ? nextComment : null,
    }
  }

  return {
    getCommentThread,
    loading,
    error: error ?? null,
  }
}
